import React, { useEffect, useState } from "react";
import Layout from "../components/app/Layout";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { ArrowLeft, Trash2, Target, Award, Activity as ActivityIcon, UserPlus, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import AuthImage from "../components/app/AuthImage";
import PhotoUpload from "../components/app/PhotoUpload";
import PlayerProgressChart from "../components/app/PlayerProgressChart";

export default function ChildProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [child, setChild] = useState(null);
  const [att, setAtt] = useState(null);
  const [progress, setProgress] = useState(null);

  const load = async () => {
    const [c, a, p] = await Promise.all([
      api.get(`/children/${id}`).catch(() => null),
      api.get(`/children/${id}/attendance`).catch(() => null),
      api.get(`/children/${id}/progress`, { params: { weeks: 8 } }).catch(() => null),
    ]);
    if (!c) { nav("/children"); return; }
    setChild(c.data);
    if (a) setAtt(a.data);
    if (p) setProgress(p.data);
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!child) {
    return (
      <Layout>
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="pitch-loader"><div className="ball" /></div>
        </div>
      </Layout>
    );
  }

  const remove = async () => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/children/${id}`);
    nav("/children");
  };

  const updatePhoto = async (url) => {
    await api.put(`/children/${id}`, { ...child, photo_url: url });
    setChild({ ...child, photo_url: url });
  };

  const Item = ({ label, value }) => (
    <div className="border border-white/10 p-4 hover:border-pitch/30 transition-colors">
      <div className="label-mono mb-1">{label}</div>
      <div className="font-oswald uppercase text-xl tracking-tight">{value || "—"}</div>
    </div>
  );

  const StatBig = ({ label, value, icon: Icon, color = "pitch" }) => (
    <div className="card-sharp p-5 relative overflow-hidden">
      <div className="flex items-center justify-between mb-2">
        <div className="label-mono">{label}</div>
        <Icon className={`w-4 h-4 text-${color}`} />
      </div>
      <div className="font-oswald text-4xl tracking-tight text-pitch">{value}</div>
    </div>
  );

  const attendancePct = att?.percentage ?? 0;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-up" style={{ opacity: 0 }} data-testid="child-profile">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-white/60 hover:text-white text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> {t("children")}
        </button>

        {/* Hero */}
        <div className="card-sharp p-6 md:p-8 relative overflow-hidden grain">
          <div className="absolute inset-0 pitch-field opacity-50" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="relative">
              {child.photo_url ? (
                <AuthImage
                  src={child.photo_url}
                  className="w-28 h-28 object-cover border-2 border-pitch"
                  fallback={<div className="w-28 h-28 bg-pitch text-white font-oswald flex items-center justify-center text-5xl">{child.jersey_number || child.name?.[0]}</div>}
                />
              ) : (
                <div className="w-28 h-28 bg-pitch text-white font-oswald flex items-center justify-center text-5xl">
                  {child.jersey_number || child.name?.[0]}
                </div>
              )}
              <div className="absolute -bottom-1 -right-1">
                <PhotoUpload value={child.photo_url} onChange={updatePhoto} size={32} testId="profile-photo-upload" />
              </div>
            </div>
            <div className="flex-1">
              <div className="label-mono text-pitch mb-1">// PLAYER PROFILE</div>
              <h1 className="font-oswald uppercase text-4xl sm:text-5xl tracking-tight leading-none">{child.name}</h1>
              <div className="flex flex-wrap gap-2 mt-3">
                {child.position && <span className="pos-badge">{child.position.slice(0, 3).toUpperCase()}</span>}
                {child.team && <span className="tag tag-pitch">{child.team}</span>}
                <span className="tag">{child.age} {lang === "bg" ? "г." : "y."}</span>
                {child.jersey_number != null && <span className="tag tag-pitch">#{child.jersey_number}</span>}
              </div>
            </div>
            {user?.role === "coach" && (
              <div className="flex flex-wrap gap-2">
                {!child.player_user_id && child.invite_status !== "pending" && (
                  <button
                    onClick={() => {
                      const email = window.prompt(lang === "bg" ? "Имейл на играча:" : "Player's email:");
                      if (!email) return;
                      api.post(`/children/${child.id}/invite`, { email }).then((r) => {
                        if (r.data.auto_linked) {
                          toast.success(lang === "bg" ? "Свързан с съществуващ профил" : "Linked to existing account");
                        } else {
                          toast.success(lang === "bg" ? "Поканата е изпратена" : "Invite sent");
                        }
                        load();
                      }).catch((e) => toast.error(e?.response?.data?.detail || "Error"));
                    }}
                    className="btn-primary px-4 py-2 flex items-center gap-2 text-sm"
                    data-testid="invite-player"
                  >
                    <UserPlus className="w-4 h-4" /> {lang === "bg" ? "Покани играч" : "Invite player"}
                  </button>
                )}
                {child.invite_status === "pending" && (
                  <span className="chip-badge amber flex items-center gap-1" data-testid="pending-chip">
                    <Mail className="w-3 h-3" />
                    {lang === "bg" ? "Покана към " : "Invited "}{child.invite_email}
                  </span>
                )}
                {child.player_user_id && (
                  <Link to={`/player/${child.player_user_id}`} className="chip-badge flex items-center gap-1" data-testid="linked-chip">
                    <Check className="w-3 h-3 text-pitch" />
                    {lang === "bg" ? "Свързан профил" : "Linked profile"}
                  </Link>
                )}
                <button onClick={remove} className="btn-secondary px-4 py-2 flex items-center gap-2 text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid="delete-child">
                  <Trash2 className="w-4 h-4" /> {t("delete")}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Performance + Attendance */}
        <div>
          <div className="label-mono mb-3">// {t("performance")}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBig label={t("goals")} value={child.goals ?? 0} icon={Target} />
            <StatBig label={t("assists")} value={child.assists ?? 0} icon={Award} />
            <StatBig label={t("matchesPlayed")} value={child.matches_played ?? 0} icon={ActivityIcon} />
            <div className="card-sharp p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="label-mono">{t("attendance")}</div>
                <ActivityIcon className="w-4 h-4 text-pitch" />
              </div>
              <div className="font-oswald text-4xl tracking-tight text-pitch">{attendancePct}%</div>
              {att && (
                <div className="mt-2">
                  <div className="h-1.5 bg-white/5 overflow-hidden">
                    <div className="h-full bg-pitch transition-all duration-700" style={{ width: `${attendancePct}%` }} />
                  </div>
                  <div className="flex justify-between text-[10px] font-mono text-white/40 mt-1.5">
                    <span>{att.attended}/{att.total_trainings}</span>
                    <span>{t("attended")}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Weekly progress chart */}
        <PlayerProgressChart data={progress?.weeks || []} lang={lang} />

        {/* Extended */}
        <div>
          <div className="label-mono mb-3">// {t("extended")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Item label={t("height")} value={child.height_cm ? `${child.height_cm} cm` : null} />
            <Item label={t("weight")} value={child.weight_kg ? `${child.weight_kg} kg` : null} />
            <Item label={t("dominantFoot")} value={child.dominant_foot} />
          </div>
          {child.medical_notes && (
            <div className="border border-white/10 p-4 mt-3">
              <div className="label-mono mb-1">{t("medicalNotes")}</div>
              <div className="text-white/80">{child.medical_notes}</div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
